import { deriveBankingMode } from "helpers/bankingStatusResolver";
import type { IPayment } from "store/paymentSlice";
import type { ITermBeneficiar } from "store/managementStore";
import type { BankingFieldKey, BankingStatusRule } from "types/countryFeatures";

/**
 * Determines whether a management-list beneficiary has complete banking data.
 * - rib must be active in acceptedPayments
 * - When internationalBankingEnabled is false, always use local banking rules regardless of bankingStatus
 *   (a bene may still carry a foreign bankingStatus from when the setting was enabled).
 * - local bankingStatus: IBAN must be filled (IBAN countries) or all required metadata fields (CA/UK)
 * - foreign bankingStatus (only when internationalBankingEnabled): intl_swift + intl_account_number must be filled
 */
export function isManagementBankInfoComplete(
    b: Pick<ITermBeneficiar, "iban" | "bankingMetadata" | "acceptedPayments" | "bankingStatus">,
    bankingFields: BankingFieldKey[],
    internationalBankingEnabled = false,
): boolean {
    if (!b.acceptedPayments?.includes("rib")) return false;
    const meta = (b.bankingMetadata ?? {}) as Record<string, string>;
    // When the international feature is disabled, force local-banking rules even if bankingStatus is foreign.
    const isLocal = !internationalBankingEnabled || !b.bankingStatus || b.bankingStatus === "local";
    if (!isLocal) {
        return !!meta.intl_swift && !!meta.intl_account_number;
    }
    if (bankingFields.includes("iban")) return !!b.iban;
    const requiredFields = bankingFields.filter((f) => f !== "iban" && f !== "bic");
    return requiredFields.length > 0 && requiredFields.every((f) => !!meta[f]);
}

export interface PaymentExportConfig {
    internationalBankingEnabled: boolean;
    bankingFields: BankingFieldKey[];
}

/**
 * True if the payment's beneficiary has all required banking info for the local wire file.
 * - Foreign beneficiaries (foreign_local / foreign_international): requires intl_swift + intl_account_number.
 * - IBAN countries: requires bankInfoComplete === 1.
 * - Non-IBAN countries: requires all non-BIC metadata fields.
 * - Legacy (feature disabled, intl_swift present, no iban): considered incomplete.
 */
export function hasLocalBankInfoComplete(payment: IPayment, config: PaymentExportConfig): boolean {
    const meta = (payment.beneficiary?.bankingMetadata ?? {}) as Record<string, string>;
    const bankingStatus = payment.beneficiary?.bankingStatus;
    const acceptedPayments = payment.beneficiary?.acceptedPayments;
    const usesIban = config.bankingFields.includes("iban");
    const requiredMetadataFields = config.bankingFields.filter((f) => f !== "iban" && f !== "bic");

    if (!config.internationalBankingEnabled && meta.intl_swift) {
        // Bene still carries intl data; complete only if local banking is also filled.
        if (config.bankingFields.includes("iban")) return !!payment.iban;
        const requiredLocalFields = config.bankingFields.filter((f) => f !== "iban" && f !== "bic");
        return requiredLocalFields.every((f) => !!meta[f]);
    }
    if (
        config.internationalBankingEnabled &&
        (bankingStatus === "foreign_local" || bankingStatus === "foreign_international")
    ) {
        if (acceptedPayments != null && !acceptedPayments.includes("rib")) return false;
        return !!(meta.intl_swift && meta.intl_account_number);
    }
    if (acceptedPayments != null && !acceptedPayments.includes("rib")) return false;
    if (usesIban) return payment.bankInfoComplete === 1;
    return requiredMetadataFields.every((f) => !!meta[f]);
}

/**
 * True if this payment should be routed to the international wire file.
 * Requires the feature flag AND bankingStatus === "foreign_international".
 */
export function isInternationalPayment(payment: IPayment, internationalBankingEnabled: boolean): boolean {
    return internationalBankingEnabled && payment.beneficiary?.bankingStatus === "foreign_international";
}

/**
 * True if the beneficiary is missing required bank data for export.
 * Payments with netRemainingToPay <= 0 are never flagged.
 */
export function isMissingBankInfo(payment: IPayment, config: PaymentExportConfig): boolean {
    if (payment.netRemainingToPay <= 0) return false;
    const meta = (payment.beneficiary?.bankingMetadata ?? {}) as Record<string, string>;
    const bankingStatus = payment.beneficiary?.bankingStatus;
    const acceptedPayments = payment.beneficiary?.acceptedPayments;

    if (!config.internationalBankingEnabled && meta.intl_swift) {
        // Bene still carries intl data; missing only if local banking is also incomplete.
        if (config.bankingFields.includes("iban")) return !payment.iban;
        const requiredLocalFields = config.bankingFields.filter((f) => f !== "iban" && f !== "bic");
        return requiredLocalFields.some((f) => !meta[f]);
    }
    if (
        config.internationalBankingEnabled &&
        (bankingStatus === "foreign_local" || bankingStatus === "foreign_international")
    ) {
        if (acceptedPayments != null && !acceptedPayments.includes("rib")) return true;
        return !(meta.intl_swift && meta.intl_account_number);
    }
    if (acceptedPayments != null && !acceptedPayments.includes("rib")) return true;
    if (!config.bankingFields.includes("iban")) {
        return config.bankingFields.filter((f) => f !== "bic").some((f) => !meta[f]);
    }
    return payment.bankInfoComplete === 0;
}

/**
 * Payments eligible for the local wire file:
 * not routed to international AND has complete local bank info.
 */
export function filterLocalFilePayments(payments: IPayment[], config: PaymentExportConfig): IPayment[] {
    return payments.filter(
        (p) => !isInternationalPayment(p, config.internationalBankingEnabled) && hasLocalBankInfoComplete(p, config),
    );
}

/**
 * Payments eligible for the international wire file.
 * Returns [] when the feature is disabled or no internationalPaymentFormatId is configured.
 */
export function filterInternationalFilePayments(payments: IPayment[], config: PaymentExportConfig): IPayment[] {
    if (!config.internationalBankingEnabled) return [];
    return payments.filter(
        (p) => p.beneficiary?.bankingStatus === "foreign_international" && hasLocalBankInfoComplete(p, config),
    );
}

/**
 * True if the payment's beneficiary is missing city or country (required fields for SEPA XML).
 * Payments with netRemainingToPay <= 0 are never flagged.
 */
export function isMissingAddress(payment: IPayment): boolean {
    if (payment.netRemainingToPay <= 0) return false;
    return !payment.beneficiary?.city || !payment.beneficiary?.country;
}

/**
 * Determines whether the IbanModal should display international fields (intl_swift / intl_account_number)
 * or the local fields (IBAN / BIC / metadata).
 */
export function deriveIbanModalMode(
    beneficiary: { bankingStatus?: string; bankingMetadata?: unknown; acceptedPayments?: string } | null | undefined,
    internationalBankingEnabled: boolean,
    companyIsoCode?: string,
    rules?: BankingStatusRule[],
): "international" | "local" {
    if (!internationalBankingEnabled || !beneficiary) return "local";
    const meta = (beneficiary.bankingMetadata ?? {}) as Record<string, string>;
    if (meta.bank_account_country && companyIsoCode) {
        return deriveBankingMode(meta.bank_account_country, companyIsoCode, rules);
    }
    if (beneficiary.bankingStatus === "foreign_local" || beneficiary.bankingStatus === "foreign_international")
        return "international";
    return "local";
}

/**
 * Returns the account number to display in the payment table:
 * - Foreign beneficiaries (feature enabled): intl_account_number
 * - IBAN countries: iban
 * - Non-IBAN countries (CA, UK…): first non-IBAN/BIC metadata field (account_number)
 */
export function deriveDisplayAccountValue(
    payment: IPayment,
    internationalBankingEnabled: boolean,
    bankingFields: BankingFieldKey[] = [],
): string {
    const bankingStatus = payment.beneficiary?.bankingStatus;
    const meta = (payment.beneficiary?.bankingMetadata ?? {}) as Record<string, string>;
    const isInternationalBeneficiary =
        internationalBankingEnabled && (bankingStatus === "foreign_local" || bankingStatus === "foreign_international");
    if (isInternationalBeneficiary) return meta.intl_account_number ?? "";
    if (payment.iban) return payment.iban;
    // For non-IBAN countries (CA, UK…): prefer account_number for display; routing fields
    // like institution_number (3 digits) and transit_number are too short to mask meaningfully.
    const routingFields: BankingFieldKey[] = ["iban", "bic", "institution_number", "transit_number", "sort_code"];
    const accountField =
        bankingFields.find((f) => f === "account_number") ?? bankingFields.find((f) => !routingFields.includes(f));
    return accountField ? meta[accountField] ?? "" : "";
}

/**
 * Determines the button/display state for the bank info cell in the payment table:
 * - "complete": bank info is present and valid → show masked account number
 * - "fix": info was entered but is incomplete/invalid (bankInfoComplete === 0)
 * - "entry": no bank info entered yet
 */
export function deriveIbanButtonState(payment: IPayment, config: PaymentExportConfig): "complete" | "fix" | "entry" {
    if (hasLocalBankInfoComplete(payment, config)) return "complete";
    if (payment.bankInfoComplete === 0) return "fix";
    return "entry";
}
